<script>
  import Button from "./Button.svelte";
  import * as transforms from "../util/transformations.js";
  import { createEventDispatcher, onMount } from "svelte";
  import { decode, encode, encodingToHex, hexToEncoding } from "bitframes";
  import Chevron from "./svg/Chevron.svelte";
  import TweakLayers from "./TweakLayers.svelte";
  import * as local from "../util/local.js";
  import TweakFunctions from "./TweakFunctions.svelte";
  import TweakFrame from "./TweakFrame.svelte";
  import TweakAdvanced from "./TweakAdvanced.svelte";

  export let edition = false;
  export let encoding = null;
  let decodedData;
  let lastEncoding;

  function encodingsMatch(a, b) {
    return encodingToHex(a) === encodingToHex(b);
  }

  let advanced = local.get("advanced", false);
  if (new URLSearchParams(window.location.search).has("advanced")) {
    advanced = true;
    local.set("advanced", true);
  }

  $: {
    if (!lastEncoding || !encodingsMatch(encoding, lastEncoding)) {
      decodedData = decode(encoding);
      lastEncoding = encoding;
      // console.log("encoding changed", decodedData);
    }
  }

  const dispatch = createEventDispatcher();

  function applyTransform(ev) {
    const fn = ev.detail;
    const enc = toCorrectSystem(
      encode({ ...decodedData, layers: fn(decodedData.layers) })
    );
    change(enc);
  }

  function applyByteTransform(ev) {
    const fn = ev.detail;
    const newBytes = toCorrectSystem(fn(encoding));
    change(newBytes);
  }

  function change(encoding) {
    dispatch("encodingChanged", encoding);
  }

  const presets = [
    ["blocky", transforms.createTextile],
    ["checker", transforms.createCheckerBoard],
    ["mono", transforms.createSolo, "byte"],
    ["glitch", transforms.createGlitch],
    ["dither", transforms.createDither, "byte"],
    ["cellular", transforms.createFractal, "byte"],
    ["dual", transforms.createDual, "byte"],
    ["weave", transforms.createScanlines, "byte"],
    ["matrix", transforms.createMorse, "byte"],
  ];
  const functions = [
    ["shuffle_colors", transforms.shuffleColors],
    ["shuffle_grid", transforms.randomGrid],
    ["shuffle_rules", transforms.shuffleRule],
    ["shuffle_scale", transforms.shuffleScale],
    ["shuffle_skip", transforms.shuffleSkip],
    ["shuffle_patterns", transforms.shufflePattern],
    ["shuffle_layers", transforms.shuffleLayers],
    ["shuffle_flip", transforms.shuffleFlip],
  ];

  let sets = [
    {
      label: "Presets",
      meta: { expanded: false },
      component: TweakFunctions,
      props: {
        functions: presets,
      },
    },
    {
      label: "Layers",
      meta: { expanded: false },
      component: TweakLayers,
    },
    {
      label: "Functions",
      meta: { expanded: false },
      component: TweakFunctions,
      props: {
        functions,
      },
    },
  ];

  if (advanced)
    sets.push(
      {
        label: "Advanced",
        meta: { expanded: false },
        component: TweakAdvanced,
        props: {},
      },
      {
        label: "Frame",
        meta: { expanded: false },
        component: TweakFrame,
        props: {},
      }
    );

  for (let s of sets) {
    const id = s.label.toLowerCase();
    const meta = local.get(`tweakpane-${id}-meta`, s.meta);
    s.meta = Object.assign(s.meta, meta);
  }

  $: sets, serialize();

  function serialize() {
    for (let s of sets) {
      const id = s.label.toLowerCase();
      local.set(`tweakpane-${id}-meta`, s.meta);
    }
  }

  function applyEncoding(ev) {
    const decodedData = ev.detail;
    let enc = encode(decodedData);
    enc = toCorrectSystem(enc);
    change(enc);
  }

  function toCorrectSystem(encoding) {
    const curSys = encoding[0];
    const newSys = edition ? 0x01 : 0x00;
    if (curSys === newSys) return encoding.slice();
    const newEncoding = encoding.slice();
    newEncoding[0] = newSys;
    return newEncoding;
  }
</script>

<div class="container">
  {#each sets as item}
    <div class="set" class:expanded={item.meta.expanded}>
      <div
        class="header"
        class:expanded={item.meta.expanded}
        on:click|nonpassive={(ev) => {
          ev.preventDefault();
          item.meta.expanded = !item.meta.expanded;
          sets = sets;
        }}
        alt={item.label}
        role="none"
      >
        <div class="header-expand">
          {#if item.meta.expanded}
            -
          {:else}
            +
          {/if}
        </div>
        <div class="header-label">{item.label}</div>
      </div>
      {#if item.meta.expanded}
        <div class="transforms">
          {#if item.component}
            <svelte:component
              this={item.component}
              {...item.props}
              on:applyTransform={applyTransform}
              on:applyByteTransform={applyByteTransform}
              on:applyEncoding={applyEncoding}
              on:encodingChanged
              {encoding}
              decoded={decodedData}
            />
          {:else}{/if}
        </div>
      {/if}
      <!-- <div class="transforms">
        {#each transforms as [name, fn]}
          <Button small text={name} on:click={applied(fn)} />
        {/each}
      </div> -->
    </div>
  {/each}
</div>

<style>
  .container {
    --gap: 20px;
    display: flex;
    justify-content: center;
    flex-direction: column;
    flex-grow: 1;
    padding: 0px;
    box-sizing: border-box;
  }
  .header {
    text-transform: uppercase;
    /* background: white; */
    display: flex;
    cursor: pointer;
    /* justify-content: space-between; */
    gap: 10px;
    align-items: center;
    padding: 10px;
    /* color: white; */
    /* padding: 0; */
    border-bottom: 1px dashed black;
    /* background: gray; */
    /* font-weight: bold; */
  }
  .header-expand {
    height: 12px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
  .header.expanded {
    border-bottom: none;
    /* background: var(--accent-color); */
  }
  .set.expanded {
    border-bottom: 1px dashed black;
  }
  @media (hover: hover) {
    .header:hover {
      /* background: hsl(0, 0%, 95%); */
    }
  }
  .set + .set {
    /* margin-top: 10px; */
  }
  .set {
    /* margin-bottom: 20px; */

    box-sizing: border-box;
    /* border-bottom: 1px solid black; */
  }
  .transforms {
    /* background: hsl(0, 0%, 90%); */
    flex-grow: 1;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0;
    padding-bottom: var(--gap);
    padding-left: var(--gap);
    padding-right: var(--gap);
    /* padding-top: 0; */
  }
</style>
